import PanelLayout from "./panel-layout";
import {useContext, useEffect, useRef, useState} from "react";
import _cloneDeep from "lodash/cloneDeep";
import useDebounce from "../hooks/use-debounce"
import Currency from "../controls/currency";
import {FormContext} from "../../index";
import axios from "axios";

export default function FilterPanelLayout(props) {
    const context = useContext(FormContext);
    const [count, setCount] = useState();
    const filters = context?.values || {};
    const cancelTokenRef = useRef(null);

    const clearSingleFilter = (field, value) => {
        context.setContext((prev) => {
            const updated = _cloneDeep(prev.values);

            if (Array.isArray(updated[field])) {
                updated[field] = updated[field].filter((v) => v !== value);
                if (updated[field].length === 0) delete updated[field];
            } else {
                delete updated[field];
            }
            return {...prev, values: updated};
        });
    };

    const clearAllFilters = () => {
        context.setContext((prev) => ({...prev, values: {}}));
    };

    const updateCount = (filtersState, cancelToken) => {
        const formValues = context.parentContext.values.values;
        const payload = {
            ...formValues,
            item: {
                ...formValues.item,
                size: 10000,
            },
            productFilter: {
                ...formValues.item.productFilter,
                filters: filtersState
            }
        };

        if (!props.uuid || !props.source || !props.baseUrl) return;

        axios.post(
            `/api/v2/website/${props.source}`,
            payload,
            {
                headers: {"X-Preview-Domain": props.baseUrl},
                cancelToken: cancelToken.token,
            }
        )
            .then(({data}) => setCount(data?.count ?? 0))
            .catch((err) => {
                if (!axios.isCancel(err)) console.error("Failed to fetch count", err);
            });
    };

    const debouncedUpdateCount = useDebounce(() => {
        // cancel any in-flight request
        if (cancelTokenRef.current) {
            cancelTokenRef.current.cancel("Cancelled due to new request");
        }

        // create a fresh token for the next query
        const token = axios.CancelToken.source();
        cancelTokenRef.current = token;

        updateCount(filters, token);
    }, 400);

    useEffect(() => {
        debouncedUpdateCount();
        return () => debouncedUpdateCount.cancel();
    }, [filters, debouncedUpdateCount]);

    const activeKeys = Object.entries(filters || {})
        .filter(([_, v]) => !(v === 0 || v === '' || (Array.isArray(v) && v.length === 0)))
        .map(([k]) => k);

    return (
        <div className="space-y-2">
            <div>{count} results</div>

            {activeKeys.length > 0 && (
                <div className="my-3">
                    <div className="-m-1 flex flex-wrap items-center">
                        {activeKeys.map((id) =>
                            Array.isArray(filters[id])
                                ? filters[id].map((value) => (
                                    <span
                                        key={id + value}
                                        className="m-1 inline-flex items-center rounded-full border border-gray-200 bg-white py-1.5 pl-3 pr-2 text-sm font-medium text-gray-900"
                                    >
                                          <span>{value}</span>
                                          <button
                                              type="button"
                                              className="ml-1 inline-flex h-4 w-4 flex-shrink-0 rounded-full p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-500"
                                              onClick={() => clearSingleFilter(id, value)}
                                          >
                                              <svg className="h-2 w-2" stroke="currentColor" viewBox="0 0 8 8">
                                                  <path strokeLinecap="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7"/>
                                              </svg>
                                          </button>
                                      </span>
                                ))
                                : (
                                    <span
                                        key={id + filters[id]}
                                        className="m-1 inline-flex items-center rounded-full border border-gray-200 bg-white py-1.5 pl-3 pr-2 text-sm font-medium text-gray-900"
                                    >
                                          <span>
                                              {id.includes('min') && 'Min '}
                                              {id.includes('max') && 'Max '}
                                              {id.includes('unitPrice') ? (
                                                  <Currency.DisplayValue
                                                      country={context?.websiteManagementContext?.country}
                                                      currency={context?.websiteManagementContext?.currency}
                                                      valueAsCents
                                                      value={+filters[id]}
                                                  />
                                              ) : id.includes('discount') ? (
                                                  'Discounted'
                                              ) : (
                                                  filters[id]
                                              )}
                                          </span>
                                          <button
                                              type="button"
                                              className="ml-1 inline-flex h-4 w-4 flex-shrink-0 rounded-full p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-500"
                                              onClick={() => clearSingleFilter(id, filters[id])}
                                          >
                                              <svg className="h-2 w-2" stroke="currentColor" viewBox="0 0 8 8">
                                                  <path strokeLinecap="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7"/>
                                              </svg>
                                          </button>
                                      </span>
                                )
                        )}
                        <button className="cursor-pointer ml-2 font-semibold text-sm" onClick={clearAllFilters}>
                            Clear Filters
                        </button>
                    </div>
                </div>
            )}

            <PanelLayout {...props} />
        </div>
    );
}